'use client'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDownToLine,
  ArrowRight,
  ArrowUpRight,
  Check,
  Compass,
  PiggyBank,
  Play,
  Search,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Utensils,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { SignedIn, SignedOut } from '@clerk/nextjs'
import { BrandLockup, useCountUp, useInView } from './atoms'

function NetWorthChart({ inView }: { inView: boolean }) {
  const [drawn, setDrawn] = useState(false)
  useEffect(() => {
    if (inView) {
      const t = setTimeout(() => setDrawn(true), 80)
      return () => clearTimeout(t)
    }
  }, [inView])

  const pts = useMemo(() => {
    const n = 28
    const base = 90_000
    const target = 127_000
    const out: number[] = []
    for (let i = 0; i < n; i++) {
      const p = i / (n - 1)
      const eased = 1 - Math.pow(1 - p, 2.2)
      const noise = Math.sin(p * 11.3) * 1400 + Math.sin(p * 4.7) * 900
      const dip = i === 14 ? -3200 : i === 9 ? -1800 : 0
      out.push(base + (target - base) * eased + noise + dip)
    }
    return out
  }, [])

  const w = 720
  const h = 188
  const xs = pts.map((_, i) => (i / (pts.length - 1)) * w)
  const min = Math.min(...pts)
  const max = Math.max(...pts)
  const ys = pts.map((v) => h - 18 - ((v - min) / (max - min)) * (h - 36))
  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L ${w} ${h} L 0 ${h} Z`

  return (
    <div className={`chart-area ${drawn ? 'drawn' : ''}`}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="nw-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent-9)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--accent-9)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            x1="0"
            x2={w}
            y1={20 + i * 45}
            y2={20 + i * 45}
            stroke="var(--neutral-4)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
        ))}
        <path className="draw-area" d={areaPath} fill="url(#nw-fill)" />
        <path
          className="draw-path"
          d={linePath}
          fill="none"
          stroke="var(--accent-9)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={1}
        />
        <circle
          cx={xs[xs.length - 1]}
          cy={ys[ys.length - 1]}
          r="5"
          fill="var(--accent-9)"
          stroke="white"
          strokeWidth="2"
        />
      </svg>
    </div>
  )
}

type KpiProps = {
  label: string
  value: number
  suffix?: string
  prefix?: string
  icon: LucideIcon
  trend?: { dir: 'pos' | 'neg'; text: string }
  decimals?: number
  inView: boolean
  barPct?: number
  delay?: number
}

function HeroKpi({
  label,
  value,
  suffix = '',
  prefix = '$',
  icon: Icon,
  trend,
  decimals = 0,
  inView,
  barPct = 70,
  delay = 0,
}: KpiProps) {
  const animated = useCountUp(value, { duration: 1800, decimals }, inView)
  const display =
    (prefix || '') +
    animated.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) +
    (suffix || '')
  const [bar, setBar] = useState(0)
  useEffect(() => {
    if (!inView) return
    const t = setTimeout(() => setBar(barPct), 250 + delay)
    return () => clearTimeout(t)
  }, [inView, barPct, delay])
  const TrendIcon = trend?.dir === 'pos' ? TrendingUp : TrendingDown
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{display}</span>
      {trend && (
        <span className={`kpi-trend ${trend.dir}`}>
          <TrendIcon size={12} />
          {trend.text}
        </span>
      )}
      <span className="kpi-icon">
        <Icon size={14} />
      </span>
      <span className="kpi-bar">
        <span className="kpi-bar-fill" style={{ width: `${bar}%` }} />
      </span>
    </div>
  )
}

function CategoryStack({ inView }: { inView: boolean }) {
  const cats = [
    { name: 'Housing', amt: 2400, color: 'var(--accent-9)' },
    { name: 'Groceries', amt: 612, color: 'var(--accent-11)' },
    { name: 'Transport', amt: 384, color: 'var(--accent-7)' },
    { name: 'Dining', amt: 298, color: 'var(--orange-9)' },
    { name: 'Utilities', amt: 215, color: 'var(--neutral-9)' },
  ]
  const max = Math.max(...cats.map((c) => c.amt))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
      {cats.map((c, i) => (
        <div
          key={c.name}
          style={{
            display: 'grid',
            gridTemplateColumns: '76px 1fr 64px',
            gap: 10,
            alignItems: 'center',
            fontSize: 11.5,
          }}
        >
          <span style={{ color: 'var(--muted-foreground)' }}>{c.name}</span>
          <span
            style={{
              height: 8,
              background: 'var(--neutral-3)',
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: inView ? `${(c.amt / max) * 100}%` : '0%',
                background: c.color,
                borderRadius: 999,
                transition: `width 1200ms cubic-bezier(0.2,0.7,0.2,1) ${300 + i * 90}ms`,
              }}
            />
          </span>
          <span
            className="mono"
            style={{
              textAlign: 'right',
              fontSize: 11.5,
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ${c.amt.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  )
}

type ChipProps = { className: string; tone: 'ok' | 'in' | 'out'; icon: LucideIcon; lbl: string; meta: string }
function FloatChip({ className, tone, icon: Icon, lbl, meta }: ChipProps) {
  return (
    <div className={`float-chip ${className}`}>
      <span className={`ic ${tone}`}>
        <Icon size={14} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span className="lbl">{lbl}</span>
        <span className="meta">{meta}</span>
      </span>
    </div>
  )
}

export function Hero() {
  const [previewRef, previewIn] = useInView<HTMLDivElement>({ threshold: 0.2 })
  const tickerTarget = 127_432.18
  const ticker = useCountUp(tickerTarget, { duration: 2200, decimals: 2 }, true)
  return (
    <header className="hero" id="top">
      <div className="hero-bg" />
      <svg className="hero-grain" aria-hidden="true">
        <filter id="grainFilter">
          <feTurbulence type="fractalNoise" baseFrequency="1.6" numOctaves={3} seed={2} />
          <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grainFilter)" />
      </svg>

      <div className="shell">
        <div className="hero-top">
          <span className="eyebrow-pill">
            <span className="dot" />
            New · AI assistant with eight financial tools
          </span>
          <h1>
            <span className="accent">ribbit.</span>
            <br />
            <span>make more </span>
            <span className="ital">money.</span>
          </h1>
          <p className="hero-sub">
            Modern double-entry bookkeeping for personal finance. Bank sync via Plaid, investment
            tracking, scenario planning, and an AI that actually reads your books.
          </p>
          <div className="hero-ctas">
            <SignedOut>
              <Link className="btn btn-primary btn-lg" href="/sign-up">
                Get started
                <span className="arr">
                  <ArrowRight size={14} />
                </span>
              </Link>
            </SignedOut>
            <SignedIn>
              <Link className="btn btn-primary btn-lg" href="/dashboard">
                Go to dashboard
                <span className="arr">
                  <ArrowRight size={14} />
                </span>
              </Link>
            </SignedIn>
            <a className="btn btn-outline btn-lg" href="#why">
              <Play size={13} />
              See it work
            </a>
          </div>
          <div className="hero-meta">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ShieldCheck size={13} /> Bank-grade encryption
            </span>
            <span className="sep" />
            <span>Free forever for accounting</span>
            <span className="sep" />
            <span>No credit card required</span>
          </div>
        </div>

        {/* Floating dashboard preview */}
        <div className="hero-preview" ref={previewRef}>
          <div className="hero-preview-frame">
            <div className="mini-nav">
              <div className="mini-nav-left">
                <BrandLockup markSize={22} wordSize={16} />
                <div style={{ width: 1, height: 18, background: 'var(--border)' }} />
                <span className="mini-tab active">Dashboard</span>
                <span className="mini-tab">Accounts</span>
                <span className="mini-tab">Transactions</span>
                <span className="mini-tab">Investments</span>
                <span className="mini-tab">Reports</span>
                <span className="mini-tab">Planning</span>
              </div>
              <div className="mini-nav-right">
                <span
                  style={{
                    color: 'var(--muted-foreground)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                  }}
                >
                  <Search size={13} /> Search
                </span>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: 'var(--accent-9)',
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  JS
                </span>
              </div>
            </div>
            <div className="mini-header">
              <span className="mini-title">Dashboard</span>
              <div className="mini-pillrow">
                <span className="mini-pill">7d</span>
                <span className="mini-pill on">30d</span>
                <span className="mini-pill">90d</span>
                <span className="mini-pill">YTD</span>
                <span className="mini-pill">All</span>
              </div>
            </div>

            <div className="mini-body">
              <div className="kpi-grid">
                <HeroKpi
                  label="Net worth"
                  value={ticker}
                  decimals={2}
                  prefix="$"
                  icon={Wallet}
                  trend={{ dir: 'pos', text: '+$4,128 · 30d' }}
                  inView={previewIn}
                  barPct={78}
                />
                <HeroKpi
                  label="Cash flow"
                  value={3284.5}
                  decimals={2}
                  prefix="+$"
                  icon={ArrowUpRight}
                  trend={{ dir: 'pos', text: '+12% MoM' }}
                  inView={previewIn}
                  barPct={62}
                  delay={120}
                />
                <HeroKpi
                  label="Savings rate"
                  value={32}
                  decimals={0}
                  prefix=""
                  suffix="%"
                  icon={PiggyBank}
                  trend={{ dir: 'pos', text: '+3 pts' }}
                  inView={previewIn}
                  barPct={64}
                  delay={240}
                />
                <HeroKpi
                  label="Runway"
                  value={18}
                  decimals={0}
                  prefix=""
                  suffix=" mo"
                  icon={Compass}
                  trend={{ dir: 'pos', text: 'comfortable' }}
                  inView={previewIn}
                  barPct={86}
                  delay={360}
                />
              </div>
              <div className="mini-row">
                <div className="mini-card">
                  <div className="mini-card-head">
                    <span className="mini-card-title">Net worth</span>
                    <span className="mini-card-desc">Last 30 days</span>
                  </div>
                  <NetWorthChart inView={previewIn} />
                </div>
                <div className="mini-card">
                  <div className="mini-card-head">
                    <span className="mini-card-title">By category</span>
                    <span className="mini-card-desc">Spend · this month</span>
                  </div>
                  <CategoryStack inView={previewIn} />
                </div>
              </div>
            </div>
          </div>

          <FloatChip className="chip-1" tone="ok" icon={Check} lbl="Synced 3 accounts" meta="just now" />
          <FloatChip
            className="chip-2"
            tone="in"
            icon={ArrowDownToLine}
            lbl="Payroll · Acme"
            meta="+$6,420.00"
          />
          <FloatChip
            className="chip-3"
            tone="out"
            icon={Utensils}
            lbl="Trader Joe's · Groceries"
            meta="−$84.31"
          />
        </div>
      </div>
    </header>
  )
}
